import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/SossietyDialog';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useCreatePromotion, useUpdatePromotion, useUserPromotions } from '@/hooks/usePromotions';
import { UserPromotion } from '@/types/promotions';
import { InstructorAutocomplete } from '@/components/ui/InstructorAutocomplete';
import { AcademyAutocomplete } from '@/components/ui/AcademyAutocomplete';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/contexts/I18nContext';
import { Input } from '@/components/ui/input';

// Belt hierarchy for validation
const BELT_ORDER = ['white', 'blue', 'purple', 'brown', 'black'];

// Belt validation helpers
const getBeltIndex = (belt: string): number => BELT_ORDER.indexOf(belt);

const validateBeltProgression = (currentBelt: string, existingPromotions: UserPromotion[]): { isValid: boolean; message?: string } => {
  if (existingPromotions.length === 0) {
    // First promotion must be white belt
    if (currentBelt !== 'white') {
      return { isValid: false, message: 'Your first promotion must be White Belt.' };
    }
    return { isValid: true };
  }

  // Get highest belt achieved
  const highestBelt = existingPromotions.reduce((highest, promotion) => {
    const highestIndex = getBeltIndex(highest);
    const currentIndex = getBeltIndex(promotion.belt_color);
    return currentIndex > highestIndex ? promotion.belt_color : highest;
  }, 'white');

  const highestBeltIndex = getBeltIndex(highestBelt);
  const currentBeltIndex = getBeltIndex(currentBelt);

  // Cannot skip belts (only show warning for actual skipping, not regression)
  if (currentBeltIndex > highestBeltIndex + 1) {
    const nextBelt = BELT_ORDER[highestBeltIndex + 1];
    return { isValid: false, message: `You must achieve ${nextBelt.charAt(0).toUpperCase() + nextBelt.slice(1)} Belt before ${currentBelt.charAt(0).toUpperCase() + currentBelt.slice(1)} Belt.` };
  }

  return { isValid: true };
};

const validateDuplicatePromotion = (
  beltColor: string, 
  stripeCount: number, 
  existingPromotions: UserPromotion[], 
  editingPromotion?: UserPromotion | null
): { isValid: boolean; message?: string } => {
  const duplicate = existingPromotions.find(promotion => 
    promotion.belt_color === beltColor && 
    promotion.stripe_count === stripeCount &&
    promotion.id !== editingPromotion?.id // Allow editing the same promotion
  );

  if (duplicate) {
    const stripeText = stripeCount === 0 ? '' : 
                     stripeCount === 1 ? ' with 1st Stripe' :
                     stripeCount === 2 ? ' with 2nd Stripe' :
                     stripeCount === 3 ? ' with 3rd Stripe' : ' with 4th Stripe';
    
    return { 
      isValid: false, 
      message: `${beltColor.charAt(0).toUpperCase() + beltColor.slice(1)} Belt${stripeText} is already registered in your profile.` 
    };
  }

  return { isValid: true };
};

interface AddPromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  promotionToEdit?: UserPromotion | null;
}

export const AddPromotionModal: React.FC<AddPromotionModalProps> = ({
  isOpen,
  onClose,
  promotionToEdit
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const createPromotionMutation = useCreatePromotion();
  const updatePromotionMutation = useUpdatePromotion();
  const { t } = useI18n();
  
  // Get existing promotions for validation
  const { data: existingPromotions = [] } = useUserPromotions(user?.id || '');

  // Form state
  const [beltColor, setBeltColor] = useState<'white' | 'blue' | 'purple' | 'brown' | 'black' | undefined>(undefined);
  const [stripeCount, setStripeCount] = useState<number>(0);
  const [graduationDate, setGraduationDate] = useState<string>('');
  const [instructorValue, setInstructorValue] = useState<any>(null);
  const [academyValue, setAcademyValue] = useState<{ id: string; name: string } | ''>('');

  // Determine if additional fields should be shown
  const shouldShowAdditionalFields = beltColor !== 'white' || stripeCount > 0;

  // Simple date mask function
  const applyDateMask = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 4) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4)}`;
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
  };

  // Simple date validation
  const isValidDate = (dateString: string) => {
    if (!dateString || dateString.length !== 10) return false;
    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year;
  };
  const [validationError, setValidationError] = useState<string>('');
  
  // User profile data for auto-filling
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  const isEditing = !!promotionToEdit;
  const isLoading = createPromotionMutation.isPending || updatePromotionMutation.isPending;

  // Helper function to handle academy value changes
  const handleAcademyChange = (value: string | { id: string; name: string }) => {
    setAcademyValue(value as { id: string; name: string } | '');
  };

  // Fetch user profile data for auto-filling
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id || !isOpen) return; // Only fetch when modal is open
      
      setIsLoadingProfile(true);
      try {

        
        // Get user profile with academy and instructors
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select(`
            *,
            main_academy:main_academy_id(id, name)
          `)
          .eq('id', user.id);



        if (profileData && profileData.length > 0) {
          const profile = profileData[0];
          // Get user's instructors (simplified approach)
          const { data: instructorsData, error: instructorsError } = await supabase
            .from('profile_instructors')
            .select('instructor_user_id')
            .eq('profile_id', user.id)
            .limit(1);



          let primaryInstructor = null;
          
          if (instructorsData && instructorsData[0]?.instructor_user_id) {
            // Get the instructor profile separately
            const { data: instructorProfiles, error: instructorError } = await supabase
              .from('profiles')
              .select('id, full_name, username')
              .eq('id', instructorsData[0].instructor_user_id);
            
            // Only set instructor if the profile exists and has a valid username
            if (instructorProfiles && instructorProfiles.length > 0 && instructorProfiles[0].username) {
              primaryInstructor = instructorProfiles[0];
            } else {
              console.warn('Instructor profile not accessible or missing username:', instructorsData[0].instructor_user_id);
            }
          }

          const userProfileData = {
            ...profile,
            primaryInstructor
          };


          setUserProfile(userProfileData);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, [user?.id, isOpen]); // Added isOpen to dependencies

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (promotionToEdit) {
        // Pre-fill form for editing
        setBeltColor(promotionToEdit.belt_color);
        setStripeCount(promotionToEdit.stripe_count);
        setGraduationDate(promotionToEdit.graduation_date || '');
        setInstructorValue(promotionToEdit.instructor_name || '');
        handleAcademyChange(promotionToEdit.school_name || '');
      } else {
        // Reset form for new promotion - NO pre-fill for white belt
        setBeltColor(undefined); // No pre-fill - user must select
        setStripeCount(0);
        setGraduationDate('');
        
        // Auto-fill instructor if user has one
        if (userProfile?.primaryInstructor) {
          setInstructorValue(userProfile.primaryInstructor);
        } else {
          setInstructorValue(null);
        }
        
        // Auto-fill academy if user has one
        if (userProfile?.main_academy) {
          setAcademyValue(userProfile.main_academy as { id: string; name: string });
        } else {
          setAcademyValue('');
        }
      }
      setValidationError('');
    } else {
      // Clear user profile when modal closes to prevent unnecessary queries
      setUserProfile(null);
    }
  }, [isOpen, promotionToEdit, userProfile]);

  // Real-time validation effect
  useEffect(() => {
    if (!beltColor || !isOpen) {
      setValidationError('');
      return;
    }

    // Validate graduation date
    if (graduationDate) {
      if (!isValidDate(graduationDate)) {
        setValidationError('Please enter a valid date in dd/mm/yyyy format.');
        return;
      }
      
      // Convert dd/mm/yyyy to Date object properly
      const [day, month, year] = graduationDate.split('/').map(Number);
      const selectedDate = new Date(year, month - 1, day); // month - 1 because Date constructor uses 0-based months
      const today = new Date();
      
      if (selectedDate > today) {
        setValidationError('Graduation date cannot be in the future.');
        return;
      }
    }

    // Validate belt progression
    const progressionValidation = validateBeltProgression(beltColor, existingPromotions);
    if (!progressionValidation.isValid) {
      setValidationError(progressionValidation.message || '');
      return;
    }

    // Validate duplicate promotion
    const duplicateValidation = validateDuplicatePromotion(beltColor, stripeCount, existingPromotions, promotionToEdit);
    if (!duplicateValidation.isValid) {
      setValidationError(duplicateValidation.message || '');
      return;
    }

    setValidationError('');
  }, [beltColor, stripeCount, graduationDate, existingPromotions, promotionToEdit, isOpen]);

  // Validate form
  const validateForm = (): boolean => {
    if (!beltColor || beltColor === undefined) {
      setValidationError('Belt color is required');
      return false;
    }

    // Check if there are any validation errors
    if (validationError) {
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Extract instructor name
      const instructorName = typeof instructorValue === 'string' 
        ? instructorValue 
        : (instructorValue && typeof instructorValue === 'object' 
          ? instructorValue.full_name || instructorValue.name || '' 
          : '');

      // Extract academy name
      const schoolName = typeof academyValue === 'string' 
        ? academyValue 
        : (academyValue && typeof academyValue === 'object' 
          ? academyValue.name || '' 
          : '');

      // Convert date from dd/mm/yyyy to ISO format
      let isoDate: string | undefined;
      if (graduationDate && isValidDate(graduationDate)) {
        const [day, month, year] = graduationDate.split('/').map(Number);
        isoDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      }

      if (isEditing && promotionToEdit) {
        await updatePromotionMutation.mutateAsync({
          promotion_id: promotionToEdit.id,
          belt_color: beltColor,
          stripe_count: stripeCount,
          graduation_date: isoDate,
          instructor_name: instructorName,
          school_name: schoolName,
        });
        toast({
          title: "Promotion updated successfully!",
          description: "Your promotion has been updated.",
          duration: 2000,
        });
      } else {
        await createPromotionMutation.mutateAsync({
          belt_color: beltColor,
          stripe_count: stripeCount,
          graduation_date: isoDate,
          instructor_name: instructorName,
          school_name: schoolName,
        });
        toast({
          title: "Promotion added successfully!",
          description: "Your new promotion has been added to your journey.",
          duration: 2000,
        });
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving promotion:', error);
      
      // Handle specific error messages
      const errorMessage = error.message;
      if (errorMessage.includes('already registered')) {
        setValidationError('This promotion is already registered in your profile.');
      } else {
        toast({
          title: "Error",
          description: `Failed to ${isEditing ? 'update' : 'add'} promotion. Please try again.`,
          variant: "destructive",
        });
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-md bg-white rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">{isEditing ? 'Edit Promotion' : 'Add Promotion'}</DialogTitle>
        <DialogDescription className="sr-only">
          {isEditing ? 'Edit your belt promotion details' : 'Add a new belt promotion to your journey'}
        </DialogDescription>
        <SossietyDialogHeader title={isEditing ? 'Edit Promotion' : 'Add Promotion'} titleClassName="text-lg">
          <SossietyDialogClose onClick={onClose} />
        </SossietyDialogHeader>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Belt Color */}
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Belt <span className="text-red-500">*</span>
            </label>
            <div className="w-full border border-black rounded-md">
              <Select 
              value={beltColor}
                onValueChange={(value) => setBeltColor(value as any)}
              >
                <SelectTrigger className="w-full text-black !ring-0 !outline-none !shadow-none border-0 focus:!ring-0 focus:!outline-none focus:!shadow-none bg-transparent relative [&_[data-radix-select-icon]]:hidden text-sm">
                  <SelectValue placeholder="Select your belt rank" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-black" />
                  </span>
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1 max-h-60">
                  <SelectItem value="white" className="text-white text-sm">White Belt</SelectItem>
                  <SelectItem value="blue" className="text-white text-sm">Blue Belt</SelectItem>
                  <SelectItem value="purple" className="text-white text-sm">Purple Belt</SelectItem>
                  <SelectItem value="brown" className="text-white text-sm">Brown Belt</SelectItem>
                  <SelectItem value="black" className="text-white text-sm">Black Belt</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stripe Count */}
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Stripe
            </label>
            <div className="w-full border border-black rounded-md">
              <Select 
                value={stripeCount.toString()} 
                onValueChange={(value) => setStripeCount(Number(value))}
              >
                <SelectTrigger className="w-full text-black !ring-0 !outline-none !shadow-none border-0 focus:!ring-0 focus:!outline-none focus:!shadow-none bg-transparent relative [&_[data-radix-select-icon]]:hidden text-sm">
                  <SelectValue placeholder="Select stripe count" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-black" />
                  </span>
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1 max-h-60">
                  <SelectItem value="0" className="text-white text-sm">No Stripe</SelectItem>
                  <SelectItem value="1" className="text-white text-sm">1st Stripe</SelectItem>
                  <SelectItem value="2" className="text-white text-sm">2nd Stripe</SelectItem>
                  <SelectItem value="3" className="text-white text-sm">3rd Stripe</SelectItem>
                  <SelectItem value="4" className="text-white text-sm">4th Stripe</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Graduation Date */}
          {shouldShowAdditionalFields && (
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Graduation Date
              </label>
              <input
                type="text"
                value={graduationDate}
                onChange={(e) => setGraduationDate(applyDateMask(e.target.value))}
                placeholder="dd/mm/yyyy"
                className="w-full p-3 bg-white border border-black rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-[#D72E16]"
              />
            </div>
          )}

          {/* Instructor */}
          {shouldShowAdditionalFields && (
            <div>
              <label className="block text-sm font-medium mb-2 text-black">
                {t('profile.instructor')}
              </label>
              {isLoadingProfile && !isEditing ? (
                <div className="w-full h-12 bg-gray-200 animate-pulse rounded-md border border-gray-300 relative">
                  <div className="absolute left-12 top-1/2 transform -translate-y-1/2 w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ) : (
                <InstructorAutocomplete
                  value={instructorValue}
                  onChange={setInstructorValue}
                  placeholder="Type a name or a username"
                  className="text-black border-black bg-white"
                  containerClassName="w-full"
                />
              )}
            </div>
          )}

          {/* Jiu Jitsu School */}
          {shouldShowAdditionalFields && (
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                {t('profile.add_your_jiu_jitsu_school')}
              </label>
              {isLoadingProfile && !isEditing ? (
                <div className="w-full h-12 bg-gray-200 animate-pulse rounded-md border border-gray-300 relative">
                  <div className="absolute left-12 top-1/2 transform -translate-y-1/2 w-40 h-3 bg-gray-300 rounded animate-pulse"></div>
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ) : (
                <AcademyAutocomplete
                  value={academyValue}
                  onChange={handleAcademyChange}
                  placeholder={t('profile.add_your_jiu_jitsu_school')}
                  className="text-black border-black bg-white"
                  containerClassName="w-full"
                />
              )}
            </div>
          )}

          {/* Validation Error */}
          {validationError && (
            <div className="text-red-500 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              {validationError}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 text-black border-black hover:bg-gray-100 rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !beltColor || beltColor === undefined || !!validationError}
              className="flex-1 bg-[#D72E16] hover:bg-[#B8251A] text-white rounded-full"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  {isEditing ? 'Updating...' : 'Adding...'}
                </span>
              ) : (
                isEditing ? 'Update' : 'Save'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 